import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * DraftAttachmentChip — visual representation of a single staged
 * attachment in the omnibar's chip row.
 *
 * One component, three branches keyed off the attachment's `type`
 * discriminator (`image` / `file` / `voice`). The visual contract:
 *
 *   • Image — small square thumbnail (aspect-ratio aware once the
 *     image decodes) with the remove button anchored to the top-right
 *     corner.
 *   • File — paperclip glyph + filename + (if present) a quiet
 *     filesize footer. Remove button as a trailing column inside the
 *     pill.
 *   • Voice — compact peaks visualisation + `M:SS` duration. Remove
 *     button as a trailing column.
 *
 * Removal is always handled by the parent — the chip is intentionally
 * dumb so the same render can live inside any container that wants to
 * show staged attachments (today: only the omnibar's chip row).
 */
import React, { memo } from "react";
import { FaIcon } from "@shared/client/components/fa-icon";
import { faXmark, faPaperclip } from "@fortawesome/pro-solid-svg-icons";
/**
 * Minimum normalised bar height for the compact voice-chip waveform.
 * Mirrors the live-meter floor (see `LiveWaveform`) so a finished
 * recording's chip and the live meter both keep their bars visible
 * during silent stretches — keeps the visual rhythm consistent
 * between "still recording" and "saved as chip".
 */
const VOICE_CHIP_MIN_BAR_HEIGHT = 0.18;
export const DraftAttachmentChip = memo(({ attachment, onRemove, }) => {
    switch (attachment.type) {
        case "image":
            return (_jsxs("div", { className: "omnibar-draft__chip-attachment omnibar-draft__chip-attachment--image", "data-type": "image", children: [_jsx("img", { className: "omnibar-draft__chip-attachment-thumb", src: attachment.previewUrl, alt: attachment.file.name }), _jsx(RemoveButton, { onClick: () => onRemove(attachment.id) })] }));
        case "file":
            return (_jsxs("div", { className: "omnibar-draft__chip-attachment omnibar-draft__chip-attachment--file", "data-type": "file", children: [_jsx("span", { className: "omnibar-draft__chip-attachment-glyph", "aria-hidden": "true", children: _jsx(FaIcon, { icon: faPaperclip }) }), _jsxs("span", { className: "omnibar-draft__chip-attachment-meta", children: [_jsx("span", { className: "omnibar-draft__chip-attachment-name", children: attachment.file.name }), attachment.file.size > 0 ? (_jsx("span", { className: "omnibar-draft__chip-attachment-detail", children: formatFileSize(attachment.file.size) })) : null] }), _jsx(RemoveButton, { onClick: () => onRemove(attachment.id) })] }));
        case "voice":
            return (_jsxs("div", { className: "omnibar-draft__chip-attachment omnibar-draft__chip-attachment--voice", "data-type": "voice", children: [_jsx("div", { className: "omnibar-draft__chip-attachment-waveform", "aria-hidden": "true", children: attachment.peaks.map((peak, i) => {
                            const height = VOICE_CHIP_MIN_BAR_HEIGHT +
                                peak * (1 - VOICE_CHIP_MIN_BAR_HEIGHT);
                            return (_jsx("span", { className: "omnibar-draft__chip-attachment-waveform-bar", style: { height: `${(height * 100).toFixed(1)}%` } }, i));
                        }) }), _jsx("span", { className: "omnibar-draft__chip-attachment-detail", children: formatDuration(attachment.durationSeconds) }), _jsx(RemoveButton, { onClick: () => onRemove(attachment.id) })] }));
    }
});
const RemoveButton = memo(({ onClick }) => {
    return (_jsx("button", { type: "button", className: "omnibar-draft__chip-attachment-remove", "aria-label": "Remove attachment", title: "Remove attachment", tabIndex: -1, 
        // Suppress focus shift so removing a chip doesn't blur the
        // editor — keeps the user's drafting flow uninterrupted.
        onMouseDown: (e) => e.preventDefault(), onClick: onClick, children: _jsx(FaIcon, { icon: faXmark }) }));
});
/**
 * Render a byte count as a short human-readable string. The omnibar
 * is bandwidth-conscious surface (capture pill, not a file manager),
 * so we cap at one decimal place and let the units do the work.
 */
function formatFileSize(bytes) {
    if (bytes < 1024)
        return `${bytes} B`;
    if (bytes < 1024 * 1024)
        return `${(bytes / 1024).toFixed(0)} KB`;
    if (bytes < 1024 * 1024 * 1024)
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}
/** Format a duration in seconds as `M:SS`. Mirrors `LiveWaveform`. */
function formatDuration(seconds) {
    const safe = Math.max(0, Math.floor(seconds));
    const m = Math.floor(safe / 60);
    const s = safe % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
}
